import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, AlertCircle, CheckCircle2 } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";

/**
 * Shared building blocks for the Organizations pages (org portal, admin
 * Organizations, student Programs tab). Same slate palette + dark mode rules
 * as the rest of the app, kept in one place instead of per-element ternaries.
 */
export function useOrgStyles() {
    const { isDarkMode: dark } = useTheme();
    return {
        dark,
        panel: dark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm",
        subPanel: dark ? "bg-slate-900/40 border-slate-700" : "bg-slate-50 border-slate-200",
        text: dark ? "text-slate-100" : "text-slate-900",
        body: dark ? "text-slate-300" : "text-slate-700",
        muted: dark ? "text-slate-400" : "text-slate-500",
        input: `w-full rounded-lg border px-3 py-2 text-sm ${dark
            ? "bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400"
            : "bg-white border-slate-300 text-slate-800 placeholder-slate-400"}`,
        th: `px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide ${dark ? "text-slate-400" : "text-slate-500"}`,
        td: `px-4 py-3 text-sm ${dark ? "text-slate-200" : "text-slate-700"}`,
        row: dark ? "border-slate-700 hover:bg-slate-700/40" : "border-slate-100 hover:bg-slate-50",
        divider: dark ? "divide-slate-700" : "divide-slate-100",
        btnPrimary: "inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50",
        btnSecondary: `inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${dark
            ? "border-slate-600 text-slate-200 hover:bg-slate-700"
            : "border-slate-300 text-slate-700 hover:bg-slate-50"}`,
        btnSm: `inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${dark
            ? "border-slate-600 text-slate-200 hover:bg-slate-700"
            : "border-slate-300 text-slate-700 hover:bg-slate-50"}`,
        btnDanger: `inline-flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-50 ${dark
            ? "text-red-300 hover:bg-red-900/30"
            : "text-red-600 hover:bg-red-50"}`,
        btnLink: `text-sm font-semibold ${dark ? "text-primary-400 hover:text-primary-300" : "text-primary-600 hover:text-primary-700"}`,
    };
}

export function PageHeader({ title, subtitle, actions }) {
    const s = useOrgStyles();
    return (
        <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div className="min-w-0">
                <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight ${s.text}`}>{title}</h1>
                {subtitle && <p className={`mt-1 ${s.muted}`}>{subtitle}</p>}
            </div>
            {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </header>
    );
}

export function Panel({ title, actions, children, className = "", padded = true }) {
    const s = useOrgStyles();
    return (
        <section className={`rounded-2xl border ${s.panel} ${className}`}>
            {(title || actions) && (
                <div className={`flex flex-wrap items-center justify-between gap-2 border-b px-5 py-4 ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                    {title && <h2 className={`text-base font-bold ${s.text}`}>{title}</h2>}
                    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
                </div>
            )}
            <div className={padded ? "p-5" : ""}>{children}</div>
        </section>
    );
}

export function EmptyState({ icon: Icon, title, body, action }) {
    const s = useOrgStyles();
    return (
        <div className="py-10 text-center">
            {Icon && (
                <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${s.dark ? "bg-slate-700/60" : "bg-slate-100"}`}>
                    <Icon size={26} className={s.dark ? "text-slate-500" : "text-slate-400"} />
                </div>
            )}
            <h3 className={`font-bold ${s.text}`}>{title}</h3>
            {body && <p className={`mx-auto mt-1 max-w-md text-sm ${s.muted}`}>{body}</p>}
            {action && <div className="mt-4">{action}</div>}
        </div>
    );
}

export function TabBar({ tabs, active, onChange, label = "Sections" }) {
    const s = useOrgStyles();
    return (
        <div className={`mb-6 overflow-x-auto rounded-2xl border p-1 ${s.panel}`} role="tablist" aria-label={label}>
            <div className="flex min-w-max gap-1">
                {tabs.map(({ key, label: tabLabel, icon: Icon }) => {
                    const isActive = active === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => onChange(key)}
                            className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${isActive
                                ? s.dark ? "bg-slate-700 text-white" : "bg-primary-50 text-primary-700"
                                : s.dark ? "text-slate-300 hover:bg-slate-700/60" : "text-slate-600 hover:bg-slate-50"}`}
                        >
                            {Icon && <Icon size={16} />}
                            <span>{tabLabel}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export function Stat({ label, value, hint, tone = "default" }) {
    const s = useOrgStyles();
    const toneClass = tone === "warn"
        ? (s.dark ? "text-amber-300" : "text-amber-600")
        : s.text;
    return (
        <div className={`rounded-2xl border p-4 ${s.panel}`}>
            <div className={`text-xs font-semibold uppercase tracking-wide ${s.muted}`}>{label}</div>
            <div className={`mt-1 text-2xl font-extrabold tabular-nums ${toneClass}`}>{value}</div>
            {hint && <div className={`mt-1 text-xs ${s.muted}`}>{hint}</div>}
        </div>
    );
}

export function Field({ label, htmlFor, hint, children, className = "" }) {
    const s = useOrgStyles();
    return (
        <div className={className}>
            <label htmlFor={htmlFor} className={`mb-1 block text-sm font-medium ${s.body}`}>{label}</label>
            {children}
            {hint && <p className={`mt-1 text-xs ${s.muted}`}>{hint}</p>}
        </div>
    );
}

export function Alert({ kind = "error", children, onClose }) {
    const s = useOrgStyles();
    if (!children) return null;
    const isError = kind === "error";
    const Icon = isError ? AlertCircle : CheckCircle2;
    const cls = isError
        ? (s.dark ? "bg-red-900/30 border-red-800 text-red-200" : "bg-red-50 border-red-200 text-red-700")
        : (s.dark ? "bg-emerald-900/30 border-emerald-800 text-emerald-200" : "bg-emerald-50 border-emerald-200 text-emerald-700");
    return (
        <div role={isError ? "alert" : "status"} className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${cls}`}>
            <Icon size={16} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">{children}</div>
            {onClose && (
                <button type="button" onClick={onClose} aria-label="Dismiss" className="opacity-70 hover:opacity-100">
                    <X size={14} />
                </button>
            )}
        </div>
    );
}

export function Pill({ children, tone = "neutral" }) {
    const s = useOrgStyles();
    const tones = {
        neutral: s.dark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600",
        primary: s.dark ? "bg-primary-900/40 text-primary-300" : "bg-primary-50 text-primary-700",
        success: s.dark ? "bg-emerald-900/40 text-emerald-300" : "bg-emerald-50 text-emerald-700",
        warning: s.dark ? "bg-amber-900/40 text-amber-300" : "bg-amber-50 text-amber-700",
        danger: s.dark ? "bg-red-900/40 text-red-300" : "bg-red-50 text-red-700",
    };
    return (
        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${tones[tone] || tones.neutral}`}>
            {children}
        </span>
    );
}

/** Dark-aware modal with a wider option (the shared ui/Modal is fixed width, light only).
 *  Portaled to <body> so parent layout utilities (e.g. space-y-* margins) can't offset it. */
export function OrgModal({ isOpen, onClose, title, children, footer, wide = false }) {
    const s = useOrgStyles();
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "unset";
            window.removeEventListener("keydown", onKey);
        };
    }, [isOpen, onClose]);
    if (!isOpen) return null;
    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={`relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-3xl shadow-2xl ${wide ? "max-w-3xl" : "max-w-lg"} ${s.dark ? "bg-slate-800" : "bg-white"}`}
            >
                <div className={`flex items-center justify-between border-b px-6 py-4 ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                    <h3 className={`text-lg font-bold ${s.text}`}>{title}</h3>
                    <button type="button" onClick={onClose} aria-label="Close" className={`rounded-full p-2 ${s.dark ? "hover:bg-slate-700" : "hover:bg-slate-100"}`}>
                        <X size={18} className={s.muted} />
                    </button>
                </div>
                <div className="overflow-y-auto px-6 py-5">{children}</div>
                {footer && (
                    <div className={`flex flex-wrap justify-end gap-2 border-t px-6 py-4 ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export function Toggle({ checked, onChange, label, description, disabled }) {
    const s = useOrgStyles();
    return (
        <label className={`flex items-start justify-between gap-4 py-2 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
            <span>
                <span className={`block text-sm font-semibold ${s.text}`}>{label}</span>
                {description && <span className={`block text-xs ${s.muted}`}>{description}</span>}
            </span>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label}
                disabled={disabled}
                onClick={() => onChange(!checked)}
                className={`relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${checked ? "bg-primary-600" : s.dark ? "bg-slate-600" : "bg-slate-300"}`}
            >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
        </label>
    );
}

export function apiError(err, fallback = "Something went wrong. Please try again.") {
    return err?.response?.data?.error || fallback;
}

export function formatDate(value) {
    if (!value) return "";
    const d = new Date(String(value).length === 10 ? `${value}T00:00:00` : value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function formatMonth(value) {
    if (!value) return "";
    const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export const ORG_TYPE_LABEL = { education: "Education", enterprise: "Enterprise" };
